import { compact, groupBy, orderBy, uniq } from 'lodash';

import { RegistrationSituationStatus, RenewalStatus } from '@appTypes/enums';
import type { OrganizationMember } from '@appTypes/esb';
import {
  findRegistrationRecord,
  getRegistrationStatus,
} from '@modules/advancement/packRoster/utilsTyped';
import {
  MemberType,
  PRIORITY_POSITION_INDEX,
} from '@modules/roster-lite/constants';

export interface RosterLiteMember {
  personGuid: string;
  memberId: string;
  userId: number;
  memberType: MemberType;
  isAdult: boolean;
  firstName: string;
  lastName: string;
  gender?: string;
  pictureUrl?: string | null;
  programs: string[];
  positions: string[];
  registrationStatus?: RegistrationSituationStatus;
  renewalStatus?: RenewalStatus;
}

type RawInput = Omit<OrganizationMember, 'registrarInfo'> & {
  registrarInfo?: OrganizationMember['registrarInfo'];
};

export function create(
  raw: RawInput,
  memberType: MemberType,
  programs: string[],
  positions: string[],
): RosterLiteMember {
  return {
    personGuid: raw.personGuid,
    memberId: raw.memberId,
    userId: raw.sbUserId,
    memberType,
    isAdult: memberType === MemberType.Adult,
    firstName: raw.firstName,
    lastName: raw.lastName,
    gender: raw.gender,
    pictureUrl: raw.pictureUrl,
    programs,
    positions,
    registrationStatus: raw.registrarInfo
      ? getRegistrationStatus(raw.registrarInfo)
      : undefined,
    renewalStatus: raw.registrarInfo?.renewalStatus,
  };
}

export function getDisplayName(m: RosterLiteMember): string {
  const lastInitial = m.lastName ? `${m.lastName[0]}.` : '';
  return `${m.firstName} ${lastInitial}`.trim();
}

export function getFullName(m: RosterLiteMember): string {
  return `${m.firstName} ${m.lastName}`.trim();
}

export function sortPositions(positions: string[]): string[] {
  return orderBy(
    positions,
    [
      p => PRIORITY_POSITION_INDEX.get(p) ?? Number.POSITIVE_INFINITY,
      p => p.toLowerCase(),
    ],
    ['asc', 'asc'],
  );
}

export function uniqueMembers(
  youths: OrganizationMember[],
  adults: OrganizationMember[],
): RosterLiteMember[] {
  const tagged = [
    ...youths.map(m => ({ ...m, _memberType: MemberType.Youth })),
    ...adults.map(m => ({ ...m, _memberType: MemberType.Adult })),
  ];
  const grouped = groupBy(tagged, 'personGuid');
  return Object.values(grouped).map(group => {
    const [first] = group;

    // findRegistrationRecord needs the date fields at the top level, so flatten
    // registrarInfo before calling it, then pull the winning registrarInfo back.
    const flatRegistrarRecords = compact(
      group.map(m =>
        m.registrarInfo
          ? {
              personGuid: m.personGuid,
              registrationExpireDt: m.registrarInfo.registrationExpireDt,
              registrationEffectiveDt: m.registrarInfo.registrationEffectiveDt,
              registrarInfo: m.registrarInfo,
            }
          : null,
      ),
    );
    const bestRegistrarInfo =
      flatRegistrarRecords.length > 0
        ? findRegistrationRecord(flatRegistrarRecords, first.personGuid)
            ?.registrarInfo
        : undefined;

    return create(
      { ...first, registrarInfo: bestRegistrarInfo },
      first._memberType,
      // compact removes empty strings before deduplicating
      uniq(compact(group.map(m => m.program))),
      sortPositions(uniq(compact(group.map(m => m.position)))),
    );
  });
}
